import { useState, useEffect, useRef } from 'react'

/**
 * Fetches and caches class metadata for a given product type.
 * The promise cache is keyed per `fetchFn` reference, so mutual funds and ETFs
 * each maintain their own cache without interfering with each other.
 *
 * Pass `null` for ETFs — metadata is derived dynamically from the product
 * summary data response inside initializeFunds, so no separate fetch is needed.
 *
 * @param {Function|null} fetchFn - Async function that returns class metadata,
 *   or null when metadata will be derived from the data response (ETFs).
 * @returns {object|null} The raw metadata response, or null while loading / when
 *   fetchFn is null.
 */
const _promiseCache = new WeakMap()

export function useFundClassMetadata(fetchFn) {
  const [data, setData] = useState(null)

  // Keep a stable ref to fetchFn so the effect dependency doesn't thrash on
  // every render (callers typically pass an inline-imported function reference
  // which is stable across renders anyway, but the ref makes it explicit).
  const fetchFnRef = useRef(fetchFn)

  useEffect(() => {
    const fn = fetchFnRef.current

    // null means this instance derives metadata from the data response instead
    // (e.g. ETFs). Nothing to fetch here.
    if (!fn) return

    if (!_promiseCache.has(fn)) {
      _promiseCache.set(
        fn,
        Promise.resolve(fn()).catch((err) => {
          // Reset so subsequent renders can retry after a failure
          _promiseCache.delete(fn)
          throw err
        })
      )
    }

    let cancelled = false
    _promiseCache.get(fn).then((result) => {
      if (!cancelled) {
        setData(result)
      }
    })

    return () => {
      cancelled = true
    }
  }, [])

  return data
}
